import React, { useState, memo, useMemo } from 'react';
import { CAP } from '../utils/permissions';
import { usePermissions } from '../utils/PermissionContext';

const SubscriptionRow = memo(({ sub, onAddToCart, emailTemplates, language, t, workspaces, extraProducts }) => {
  // Add licences, the renewal nudge and the trial conversion all put something
  // in the cart, which a caller without `purchase` has no cart for. The whole
  // last column goes rather than showing an "Add licences" header over empty
  // cells.
  const { can } = usePermissions();
  const canPurchase = can(CAP.PURCHASE);
  const isTrial = useMemo(() => {
    return !!sub.planName && sub.planName.toUpperCase().includes('TRIAL');
  }, [sub.planName]);

  const isAnnual = useMemo(() => {
    return sub.planName && sub.planName.toUpperCase().includes('ANNUAL');
  }, [sub.planName]);

  // For a trial plan, find the matching paid annual product in the catalog so the
  // customer can self-subscribe to the annual version of this same SKU.
  const annualProduct = useMemo(() => {
    if (!isTrial) return null;
    const catalog = [...(workspaces || []), ...(extraProducts || [])];
    return catalog.find((p) => p.related_licenses_sku === sub.skuId) || null;
  }, [isTrial, workspaces, extraProducts, sub.skuId]);

  const annualPrice = annualProduct ? parseFloat(annualProduct.price) : NaN;
  const hasAfasCode = !!annualProduct && (annualProduct.afas_artikelcode ?? '').toString().trim() !== '';
  // Only offer self-subscribe when the annual product is fully billable (priced and
  // has an AFAS article code) — the same guard WorkspaceGrid applies to its cards.
  const canConvert = isTrial && !!annualProduct && !isNaN(annualPrice) && annualPrice > 0 && hasAfasCode;

  // Prefill the quantity with the current trial seat count for trial rows.
  const [quantity, setQuantity] = useState(() => (
    isTrial ? String(sub.purchasedLicenses || sub.assignedLicenses || 1) : ''
  ));

  const handleQuantityChange = (value) => {
    const num = parseInt(value, 10);
    setQuantity(value === '' ? '' : (isNaN(num) || num < 1 ? 1 : num));
  };

  const handleAddToCart = (isRenewal) => {
    if (!isRenewal && (!quantity || quantity <= 0)) {
      return;
    }

    onAddToCart({
      id: sub.subscriptionId,
      name: sub.skuName,
      quantity: isRenewal ? (sub.assignedLicenses !== undefined && sub.assignedLicenses !== null ? sub.assignedLicenses : sub.purchasedLicenses) : quantity,
      type: 'license',
      planName: sub.planName,
      renewalDate: sub.renewalDate,
      skuId: sub.skuId,
      isRenewal: isRenewal === true
    });
    setQuantity('');
  };

  // Subscribe a trial customer to the paid annual plan for this SKU. Mirrors the
  // brand-new-annual cart item built by WorkspaceGrid: full annual price and no
  // renewalDate, which the cart reducer prices at the full year, not pro-rated.
  const handleConvertTrial = () => {
    if (!canConvert) return;
    const num = parseInt(quantity, 10);
    const finalQty = isNaN(num) || num <= 0 ? 1 : num;

    onAddToCart({
      id: sub.subscriptionId,
      name: sub.skuName,
      skuId: sub.skuId,
      type: 'license',
      planName: 'ANNUAL_YEARLY_PAY',
      isRenewal: false,
      price: annualPrice,
      quantity: finalQty,
      related_licenses_sku: sub.skuId,
      afasArticleCode: annualProduct.afas_artikelcode || ''
    });
    setQuantity('');
  };

  const activeTemplate = useMemo(() => {
    if (!sub.showRenewalReminder || sub.daysUntilRenewal === undefined) {
      return null;
    }

    // For auto-renewing annual plans on their exact renewal date (daysUntilRenewal === 0),
    // we give a 1-day grace period (treat it as 1) to allow Google's backend to process 
    // the renewal. This prevents triggering the 0-day "suspended" template prematurely.
    let effectiveDays = sub.daysUntilRenewal;
    if (isAnnual && effectiveDays === 0) {
      effectiveDays = 1;
    }

    const reminders = t.renewal_reminders;
    if (!reminders) return null;

    let bodyToDisplay = null;
    if (effectiveDays <= -7) {
      bodyToDisplay = reminders.lost;
    } else if (effectiveDays <= 0) {
      bodyToDisplay = reminders.days_0;
    } else if (effectiveDays <= 2) {
      bodyToDisplay = reminders.days_2;
    } else if (effectiveDays <= 7) {
      bodyToDisplay = reminders.days_7;
    } else if (effectiveDays <= 15) {
      bodyToDisplay = reminders.days_15;
    } else if (effectiveDays <= 30) {
      bodyToDisplay = reminders.days_30;
    }

    return bodyToDisplay;
  }, [sub.showRenewalReminder, sub.daysUntilRenewal, isAnnual, t.renewal_reminders]);

  return (
    <React.Fragment>
      <tr className={(!isAnnual && !canConvert) ? "opacity-50 bg-gray-50/30 select-none" : ""}>
        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{sub.skuName}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{sub.purchasedLicenses}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{sub.assignedLicenses ?? t.not_applicable}</td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {sub.renewalDate ? (
            <div className="flex items-center gap-2">
              <span>{sub.renewalDate}</span>
              {(['AUTO_RENEW', 'RENEW_CURRENT_COMMITMENT', 'AUTO_RENEW_YEARLY_PAY', 'AUTO_RENEW_MONTHLY_PAY'].includes(sub.renewalType) || (typeof sub.renewalType === 'string' && sub.renewalType.toUpperCase().includes('AUTO_RENEW'))) && (
                <span className="px-1.5 py-0.5 rounded text-xs font-bold text-red-500 border border-red-500 uppercase">
                  auto renew
                </span>
              )}
            </div>
          ) : sub.planName ? (
            <span className="px-2 py-0.5 rounded-md text-xs font-mono font-bold bg-gray-100 border border-gray-200 text-gray-700 inline-block">
              {sub.planName.toUpperCase() === 'FLEXIBLE' ? 'FLEX' : sub.planName.toUpperCase()}
            </span>
          ) : (
            t.not_applicable
          )}
        </td>
        {canPurchase && <td className="px-6 py-4 whitespace-nowrap text-sm">
          {isAnnual ? (
            <div className="flex items-center gap-6">
              <input
                type="number"
                min="1"
                className="w-24 border border-gray-300 rounded-md p-1.5 focus:ring-primary focus:border-primary sm:text-sm"
                placeholder={t.quantity_placeholder || "Aantal"}
                value={quantity}
                onChange={(e) => handleQuantityChange(e.target.value)}
              />
              <button
                onClick={() => handleAddToCart(false)}
                title={t.add_to_cart}
                className="text-blue-600 hover:text-blue-800 disabled:text-gray-300 flex-shrink-0"
                disabled={!quantity}
              >
                <svg className="w-6 h-6" viewBox="0 0 500 500" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor">
                  <g opacity="0.5">
                    <path d="M156.25 468.751C173.509 468.751 187.5 454.76 187.5 437.501C187.5 420.242 173.509 406.251 156.25 406.251C138.991 406.251 125 420.242 125 437.501C125 454.76 138.991 468.751 156.25 468.751Z" fill="#161616" />
                    <path d="M375 468.751C392.259 468.751 406.25 454.76 406.25 437.501C406.25 420.242 392.259 406.251 375 406.251C357.741 406.251 343.75 420.242 343.75 437.501C343.75 454.76 357.741 468.751 375 468.751Z" fill="#161616" />
                    <path d="M437.5 109.376H90.9375L78.125 43.7506C77.3945 40.1682 75.4309 36.9554 72.5759 34.6714C69.7209 32.3874 66.1554 31.1769 62.5 31.2506H0V62.5006H49.6875L109.375 362.501C110.105 366.083 112.069 369.296 114.924 371.58C117.779 373.864 121.345 375.074 125 375.001H406.25V343.751H137.812L125 281.251H406.25C409.862 281.339 413.393 280.173 416.242 277.951C419.09 275.729 421.081 272.588 421.875 269.063L453.125 128.438C453.649 126.12 453.636 123.713 453.089 121.4C452.542 119.087 451.475 116.929 449.968 115.091C448.461 113.253 446.555 111.782 444.395 110.792C442.234 109.802 439.876 109.317 437.5 109.376ZM393.75 250.001H119.062L97.1875 140.626H417.969L393.75 250.001Z" fill="#161616" />
                  </g>
                </svg>
              </button>
              {sub.showRenewalReminder && sub.daysUntilRenewal >= -7 && (
                <button
                  onClick={() => handleAddToCart(true)}
                  className="flex items-center text-red-600 border border-red-200 bg-red-50 px-2.5 py-1.5 rounded text-xs font-semibold shadow-sm whitespace-nowrap hover:bg-red-100 disabled:opacity-50 animate-breath-shadow-red h-8 ml-1"
                >
                  <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                  {t.renew_reminder ? t.renew_reminder.replace('{days}', sub.daysUntilRenewal) : `Renew in ${sub.daysUntilRenewal} days`}
                </button>
              )}
            </div>
          ) : canConvert ? (
            <div className="flex items-center gap-4">
              <input
                type="number"
                min="1"
                className="w-24 border border-gray-300 rounded-md p-1.5 focus:ring-primary focus:border-primary sm:text-sm"
                placeholder={t.quantity_placeholder || "Aantal"}
                value={quantity}
                onChange={(e) => handleQuantityChange(e.target.value)}
              />
              <button
                onClick={handleConvertTrial}
                disabled={!quantity}
                title={t.subscribe_to_annual || "Subscribe (annual)"}
                className="flex items-center gap-2 bg-primary text-white px-3 py-1.5 rounded-md text-sm font-semibold hover:bg-primary-hover transition duration-300 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 500 500" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M156.25 468.751C173.509 468.751 187.5 454.76 187.5 437.501C187.5 420.242 173.509 406.251 156.25 406.251C138.991 406.251 125 420.242 125 437.501C125 454.76 138.991 468.751 156.25 468.751Z" fill="currentColor" />
                  <path d="M375 468.751C392.259 468.751 406.25 454.76 406.25 437.501C406.25 420.242 392.259 406.251 375 406.251C357.741 406.251 343.75 420.242 343.75 437.501C343.75 454.76 357.741 468.751 375 468.751Z" fill="currentColor" />
                  <path d="M437.5 109.376H90.9375L78.125 43.7506C77.3945 40.1682 75.4309 36.9554 72.5759 34.6714C69.7209 32.3874 66.1554 31.1769 62.5 31.2506H0V62.5006H49.6875L109.375 362.501C110.105 366.083 112.069 369.296 114.924 371.58C117.779 373.864 121.345 375.074 125 375.001H406.25V343.751H137.812L125 281.251H406.25C409.862 281.339 413.393 280.173 416.242 277.951C419.09 275.729 421.081 272.588 421.875 269.063L453.125 128.438C453.649 126.12 453.636 123.713 453.089 121.4C452.542 119.087 451.475 116.929 449.968 115.091C448.461 113.253 446.555 111.782 444.395 110.792C442.234 109.802 439.876 109.317 437.5 109.376ZM393.75 250.001H119.062L97.1875 140.626H417.969L393.75 250.001Z" fill="currentColor" />
                </svg>
                {t.subscribe_to_annual || "Subscribe (annual)"}
              </button>
              <span className="text-xs text-gray-400 font-medium whitespace-nowrap">
                € {annualPrice.toFixed(2)} {t.per_license_year || "per license / year"}
              </span>
            </div>
          ) : null}
        </td>}
      </tr>
      {activeTemplate && (
        <tr>
          <td colSpan={canPurchase ? 5 : 4} className="px-6 pb-4 pt-1 border-t-0">
            <div className="text-sm text-gray-700 italic border-l-4 border-red-400 pl-4 py-3 bg-red-50 rounded-r-lg w-full">
              <span dangerouslySetInnerHTML={{ __html: activeTemplate }} className="block w-full" />
            </div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );
});

function SubscriptionsTable({ subscriptions, onAddToCart, onRenewAll, emailTemplates, language, t, workspaces, extraProducts }) {
  const { can } = usePermissions();
  const canPurchase = can(CAP.PURCHASE);
  return (
    <div className="bg-white rounded-lg mb-8 overflow-x-auto tour-subscriptions-table">
      <table className="min-w-full divide-y divide-gray-200">
        <thead>
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider bg-primary-custom">{t.product}</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider bg-primary-custom">{t.total}</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider bg-primary-custom">{t.in_use}</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider bg-primary-custom">{t.renewal_date}</th>
            {canPurchase && <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider bg-primary-custom">{t.add_licenses}</th>}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {subscriptions.map(sub => (
            <SubscriptionRow
              key={sub.subscriptionId}
              sub={sub}
              onAddToCart={onAddToCart}
              emailTemplates={emailTemplates}
              language={language}
              t={t}
              workspaces={workspaces}
              extraProducts={extraProducts}
            />
          ))}
        </tbody>
      </table>
      {canPurchase && onRenewAll && subscriptions.some(sub => sub.showRenewalReminder && sub.daysUntilRenewal >= -7) && (
        <div className="p-4 border-t border-gray-200 flex justify-end bg-gray-50">
          <button
            onClick={(e) => {
              e.preventDefault();
              onRenewAll(subscriptions);
            }}
            className="bg-primary text-white px-6 py-2 border-2 border-primary rounded-md hover:bg-primary-hover transition duration-300 ease-in-out font-semibold"
          >
            {t.renew_all_licenses || "Renew all licenses"}
          </button>
        </div>
      )}

      {/* {(
        <div className="p-4 border-t border-gray-200 flex justify-end bg-gray-50">
          <button
            onClick={(e) => {
              e.preventDefault();
              onRenewAll(subscriptions);
            }}
            className="bg-primary text-white px-6 py-2 border-2 border-primary rounded-md hover:bg-primary-hover transition duration-300 ease-in-out font-semibold"
          >
            {t.renew_all_licenses || "Renew all licenses"}
          </button>
        </div>
      )} */}
    </div>
  );
}

export function SubscriptionsTableSkeleton({ t }) {
  // Same column count as the real table, so the layout does not shift when the
  // data lands for a caller who cannot buy.
  const { can } = usePermissions();
  const canPurchase = can(CAP.PURCHASE);
  return (
    <div className="bg-white rounded-lg mb-8 overflow-x-auto shadow-sm border border-gray-100 animate-pulse">
      <table className="min-w-full divide-y divide-gray-200">
        <thead>
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider bg-primary-custom">{t.product}</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider bg-primary-custom">{t.total}</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider bg-primary-custom">{t.in_use}</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider bg-primary-custom">{t.renewal_date}</th>
            {canPurchase && <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider bg-primary-custom">{t.add_licenses}</th>}
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-100">
          {[1, 2].map((row) => (
            <tr key={row}>
              <td className="px-6 py-5 whitespace-nowrap">
                <div className="h-4 bg-gray-200 rounded w-48"></div>
              </td>
              <td className="px-6 py-5 whitespace-nowrap">
                <div className="h-4 bg-gray-200 rounded w-8"></div>
              </td>
              <td className="px-6 py-5 whitespace-nowrap">
                <div className="h-4 bg-gray-200 rounded w-8"></div>
              </td>
              <td className="px-6 py-5 whitespace-nowrap">
                <div className="h-4 bg-gray-200 rounded w-24"></div>
              </td>
              {canPurchase && <td className="px-6 py-5 whitespace-nowrap">
                <div className="flex items-center gap-4">
                  <div className="h-8 bg-gray-200 rounded w-24"></div>
                  <div className="h-8 w-8 bg-gray-200 rounded-full"></div>
                </div>
              </td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default SubscriptionsTable;
